import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeviceUnknownIcon from '@mui/icons-material/DeviceUnknown';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { Box, Stack, Typography } from '@mui/material';
import { isNull } from 'lodash';
import { cloneElement } from 'react';

import { useDevice, useDeviceSdCardStatus } from '@/api/devices';
import { ErrorIcon } from '@/components/StatusIcons';
import { TetherInfoAlert } from '@/components/TetherInfoAlert';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { BatterySection } from '@/components/spotter/statusTab/BatterySection';
import { DeviceFirmwareSection } from '@/components/spotter/statusTab/DeviceFirmwareSection';
import { TelemetrySection } from '@/components/spotter/statusTab/TelemetrySection';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTetherDeviceIdentity } from '@/hooks/useTetherDeviceIdentity';
import { Nullable, checkForSystemError } from '@/utils';

const border = '1px solid rgba(192, 192, 192, 0.8)';

const systemStatusesMap = {
    'system-ok': {
        systemStatusIcon: <CheckCircleIcon />,
        color: 'success',
        message: 'System is not reporting any errors',
    },
    'system-unknown': {
        systemStatusIcon: <DeviceUnknownIcon />,
        color: 'warning',
        message: 'Unknown system status',
    },
    'system-error': { systemStatusIcon: <ErrorIcon />, color: 'error', message: 'System error. Contact Sofar.' },
};

const processDeviceStatus = (status?: Nullable<number>) => {
    if (isNull(status)) {
        return { ...systemStatusesMap['system-ok'], description: '' };
    }
    const { hasError, description } = checkForSystemError(status);
    if (hasError) {
        return { ...systemStatusesMap['system-error'], description };
    }
    return { ...systemStatusesMap['system-ok'], description: '' };
};

export const DeviceStatusSection = () => {
    const { spotterId } = useSpotterContext();
    const { data: device, isPending: isDevicesPending } = useDevice({
        spotterId,
    });

    const {
        systemStatusIcon,
        color: systemStatusColor,
        message,
        description: systemStatusDescription,
    } = processDeviceStatus(device?.status);

    const { data: sdCardStatus } = useDeviceSdCardStatus({ spotterId });
    const { isScout, tetherDashboardUrl } = useTetherDeviceIdentity();

    if (isDevicesPending) {
        return <GenericSkeleton lines={3} />;
    }

    return (
        <Stack gap={4}>
            {isScout && (
                <TetherInfoAlert title="View Scout performance data on Tether" href={tetherDashboardUrl}>
                    To see and download Scout performance such as battery history, heading, and speed, go to the Tether
                    Dashboard.
                </TetherInfoAlert>
            )}
            <Typography variant="h3">System Status</Typography>
            <BatterySection />
            <TelemetrySection />
            <Stack>
                <Typography variant="h4" mb={2}>
                    System
                </Typography>
                <Box sx={{ border, p: 3 }}>
                    <Stack gap={2}>
                        <Stack direction="row" gap={1} alignItems="center">
                            {cloneElement(systemStatusIcon, { color: systemStatusColor })}
                            <Typography variant="body2">
                                {message} {systemStatusDescription}
                            </Typography>
                        </Stack>
                        {device?.currentMode === 'deployment' && (
                            <Stack direction="row" gap={1} alignItems="center">
                                <DoNotDisturbOnIcon color="warning" />
                                <Typography variant="body2">Spotter is in idle mode</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Box>
                {sdCardStatus && (
                    <Box sx={{ border, p: 3, borderTop: 'none' }}>
                        <Stack gap={2}>
                            <Typography variant="h5">SD card status</Typography>
                            <Stack direction="row" gap={1} alignItems="center">
                                {sdCardStatus.StatusIcon}
                                <Typography variant="body2">{sdCardStatus.label}</Typography>
                            </Stack>
                        </Stack>
                    </Box>
                )}
                <Box sx={{ border, p: 3, borderTop: 'none' }}>
                    <DeviceFirmwareSection />
                </Box>
            </Stack>
            <Stack gap={2}></Stack>
        </Stack>
    );
};
